import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@winnin/winnin_ui.components.button'
import { InputWithLabel } from '@winnin/winnin_ui.components.input-with-label'
import { useToast } from '@winnin/winnin_ui.components.toast'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import LinkToast from 'components/Toasts/LinkToast'
import { useAuth } from 'contexts/AuthContext'
import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { useDashboardsQueries } from 'hooks-queries/dashboards'
import { customerioTracker } from 'lib'
import { T, useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { TOLGEE_GLOBALS, analyticsTracker } from 'utils'

import { GET_WORKSPACE_JOBS_COUNT_LOG, TOLGEE_PREFIX, MAX_DASHBOARD_DESCRIPTION_LENGTH } from './consts'
import { CREATE_DASHBOARD_MUTATION } from '../../query'
import TerritorySelector from '../TerritorySelector'

import './index.scss'

const getSuccessToastMessage = ({ jobName }) => {
  return (
    <p>
      <T
        keyName={`${TOLGEE_PREFIX}-successDashboardCreationToast`}
        parameters={{
          jobName: jobName || '',
          strong: <strong className="toast-jobName" />,
        }}
      >
        {'Dashboard <strong>{jobName}</strong> was successfully created!'}
      </T>
    </p>
  )
}

export default function CreateDashboardModalSecondStep() {
  const t = useTranslate()
  const { createToast } = useToast()
  const { plan } = useAuth()
  const { dashboard, setDashboard, setIsCreateDashboardModalOpen, setCreateDashboardStep } = useDashboardModals()

  const workspaceId = PanelStore?.selectedPanel?.id

  const [territories, setTerritories] = useState({})

  const { data, loading: loadingCount } = useDashboardsQueries({
    queryName: 'getWorkspaceJobsCount',
    errorLog: GET_WORKSPACE_JOBS_COUNT_LOG,
  })

  const [createDashboard, { loading }] = useMutation(CREATE_DASHBOARD_MUTATION, {
    onCompleted({ createWorkspaceJob }) {
      setDashboard({})
      setCreateDashboardStep(1)
      setIsCreateDashboardModalOpen(false)

      const infoToast = {
        message: getSuccessToastMessage({ jobName: createWorkspaceJob?.name }),
        link: `/workspace/${workspaceId}/jobs/${createWorkspaceJob?.id}/dashboard`,
      }

      const { CREATE_JOB } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(CREATE_JOB, {
        'Job Created': true,
        'Job Name': createWorkspaceJob?.name,
        'Job ID': createWorkspaceJob?.id,
        'Job Type': createWorkspaceJob?.type,
        ...(createWorkspaceJob?.description && { 'Job Description': createWorkspaceJob?.description }),
      })

      const { CREATED_DASHBOARD } = customerioTracker.eventNames
      customerioTracker.track(CREATED_DASHBOARD, {
        job_id: createWorkspaceJob?.id,
      })

      createToast({
        content: <LinkToast toast={infoToast} />,
      })
    },
    refetchQueries: [
      'workspaceJobsWithFoldersPaginated',
      'addToGetWorkspaceJobsPaginated',
      'getListWorkspaceJobPaginated',
    ],
    onError(error) {
      console.error('CreateDashboardModalSecondStep @ createDashboard', error)
      createToast({ content: t(TOLGEE_GLOBALS.somethingWentWrong) })
    },
  })

  const handleGoalChange = e => {
    setDashboard(prevState => ({
      ...prevState,
      description: e.target.value,
    }))
  }

  const handleCancelButtonClick = () => {
    setDashboard({})
    setCreateDashboardStep(1)
    setIsCreateDashboardModalOpen(false)
  }

  const handleBackButtonClick = () => {
    setDashboard(prevState => ({
      ...prevState,
      territories: [],
      description: '',
    }))
    setCreateDashboardStep(1)
  }

  const handleCreateButtonClick = () => {
    const initialTerritoriesTopics = territories?.topics?.map(territory => `topic.${territory?.value}`) || []
    const initialTerritoriesSubtopics = territories?.subtopics?.map(territory => `subtopic.${territory?.value}`) || []
    const initialTerritoriesSavedPostSearches =
      territories?.savedPostSearches?.map(territory => `saved_search.${territory?.value}`) || []
    const initialGlobalTopics = territories?.global_topic?.map(item => item?.value) || []

    const initialTerritories = [
      ...initialTerritoriesTopics,
      ...initialTerritoriesSubtopics,
      ...initialTerritoriesSavedPostSearches,
      ...initialGlobalTopics,
    ]

    createDashboard({
      variables: {
        workspaceJobInput: {
          name: dashboard?.name,
          type: dashboard?.useCase,
          description: dashboard?.description,
          shouldCreateDashboard: true,
          initialTerritories,
        },
      },
    })
  }

  const isCreateButtonDisabled = !dashboard?.description

  const maxJobs = plan?.jobs
  const currentDashCount = data?.listWorkspaceJobPaginated?.totalCount ?? 0
  const canCreateDash = currentDashCount < maxJobs

  const tooltipMessage = useMemo(() => {
    if (!canCreateDash) {
      return t(TOLGEE_GLOBALS.dashboardLimitMessage)
    }

    if (isCreateButtonDisabled) {
      return t({
        key: `${TOLGEE_PREFIX}-requiredFieldsWarning`,
        defaultValue: 'Para prosseguir com a criação do dashboard é necessário informar o objetivo do mesmo',
      })
    }

    return ''
  }, [canCreateDash, isCreateButtonDisabled, t])

  return (
    <div className="CreateDashboardModalSecondStep">
      <div>
        <label className="CreateDashboardModalSecondStep-label" htmlFor="dashboard-description">
          <T keyName={`${TOLGEE_PREFIX}-dashboardDescriptionLabel`}>Qual o objetivo?</T>
        </label>
        <InputWithLabel
          id="dashboard-description"
          onChange={e => handleGoalChange(e)}
          value={dashboard?.description}
          placeholder={t({
            key: `${TOLGEE_PREFIX}-dashboardDescriptionPlaceholder`,
            defaultValue: 'Crie uma descrição explicando mais',
          })}
          maxLength={MAX_DASHBOARD_DESCRIPTION_LENGTH}
        />
      </div>

      {dashboard?.useCase !== 'other' && (
        <div>
          <p className="CreateDashboardModalSecondStep-label">
            <T keyName={`${TOLGEE_PREFIX}-dashboardTerritoriesLabel`}>Quais são os seus territórios de interesse?</T>
          </p>
          <TerritorySelector
            filters={territories}
            setFilters={setTerritories}
            subtitle={
              <T keyName={`${TOLGEE_PREFIX}-dashboardTerritoriesSubtitle`}>
                Esses territórios serão automaticamente salvos dentro do seu novo dashboard
              </T>
            }
          />
        </div>
      )}

      <div className="CreateDashboardModalSecondStep-footer">
        <Button onClick={handleBackButtonClick} variation="faded" appearance="link">
          {t(TOLGEE_GLOBALS.back)}
        </Button>

        <Button onClick={handleCancelButtonClick} variation="faded" appearance="link">
          {t(TOLGEE_GLOBALS.cancel)}
        </Button>

        <TooltipWrapper tooltipMessage={tooltipMessage} tooltipStyle="tooltippedN" isTooltipMultiline>
          <Button
            onClick={handleCreateButtonClick}
            disabled={!canCreateDash || isCreateButtonDisabled || loading || loadingCount}
          >
            {t(TOLGEE_GLOBALS.create)}
          </Button>
        </TooltipWrapper>
      </div>
    </div>
  )
}
